import {useEffect, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {GET_BOTMAKER_SETTINGS} from '../../gql/queries/botmaker'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {defineMessages, MessageDescriptor, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {CREATE_BOTMAKER_WHATSAPP_TEMPLATE} from '../../gql/mutations/botmaker'
import {WhatsAppPreview} from '../templates/components/tiptap/WhatsAppPreview'
import {WhatsAppEditor} from '../templates/components/tiptap/WhatsAppEditor'
import {BOTMAKER_VARIABLES, DEFAULT_TEMPLATE_VARIABLES} from '../templates/variables'

const M = defineMessages({
  title: {id: 'integrations.botmaker.title', defaultMessage: 'Create Botmaker template'},
  botLine: {id: 'integrations.botmaker.botLine', defaultMessage: 'Bot {name} ({phone})'},

  namePlaceholder: {
    id: 'integrations.botmaker.namePlaceholder',
    defaultMessage: 'payment_reminder',
  },
  locale: {id: 'integrations.template.locale', defaultMessage: 'Locale'},
  category: {id: 'integrations.template.category', defaultMessage: 'Category'},
  namespace: {id: 'integrations.botmaker.namespace', defaultMessage: 'Namespace (WhatsApp)'},
  namespacePlaceholder: {
    id: 'integrations.botmaker.namespacePlaceholder',
    defaultMessage: 'e.g. f1a2b3c4_d5e6_7f8g...',
  },

  headerSection: {
    id: 'integrations.botmaker.headerSection',
    defaultMessage: 'Header (multimedia)',
  },
  headerNone: {id: 'integrations.botmaker.header.none', defaultMessage: 'None'},
  headerUrlPlaceholder: {
    id: 'integrations.botmaker.headerUrlPlaceholder',
    defaultMessage: 'Media URL (e.g. https://...)',
  },

  bodyMessage: {id: 'integrations.botmaker.bodyMessage', defaultMessage: 'Body message'},
  bodyPlaceholder: {id: 'integrations.botmaker.bodyPlaceholder', defaultMessage: 'Hi ...'},
  footerPlaceholder: {
    id: 'integrations.botmaker.footerPlaceholder',
    defaultMessage: 'Add a short footer text...',
  },

  buttons: {id: 'integrations.botmaker.buttons', defaultMessage: 'Buttons'},
  addButton: {id: 'integrations.botmaker.addButton', defaultMessage: 'Add button'},
  quickReply: {id: 'integrations.botmaker.quickReply', defaultMessage: 'Quick reply'},
  intentPlaceholder: {
    id: 'integrations.botmaker.intentPlaceholder',
    defaultMessage: 'Intent ID / Name',
  },
  valuePlaceholder: {id: 'integrations.botmaker.valuePlaceholder', defaultMessage: 'Value'},
  noButtons: {id: 'integrations.botmaker.noButtons', defaultMessage: 'No buttons added.'},
  buttonFallback: {id: 'integrations.botmaker.buttonFallback', defaultMessage: 'Button'},

  previewPlaceholder: {
    id: 'integrations.botmaker.previewPlaceholder',
    defaultMessage: 'Start typing to see preview...',
  },

  optInImage: {
    id: 'integrations.botmaker.optInImage',
    defaultMessage: 'Opt-in image URL (required by Botmaker)',
  },
  optInImagePlaceholder: {
    id: 'integrations.botmaker.optInImagePlaceholder',
    defaultMessage: 'https://...',
  },
  optInImageHint: {
    id: 'integrations.botmaker.optInImageHint',
    defaultMessage: "URL of the image that proves the user's consent (required for Marketing).",
  },

  submit: {id: 'integrations.template.create', defaultMessage: 'Create template'},

  toastSubmitted: {
    id: 'integrations.botmaker.toast.submitted',
    defaultMessage: 'Template submitted to Botmaker',
  },
  toastCreateError: {
    id: 'integrations.botmaker.toast.createError',
    defaultMessage: 'Error creating template',
  },
  toastBodyRequired: {
    id: 'integrations.botmaker.toast.bodyRequired',
    defaultMessage: 'Body text is required',
  },

  // Reusadas
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  footerOptional: {id: 'integrations.template.footerOptional', defaultMessage: 'Footer (optional)'},
  headerImage: {id: 'comms.templates.wa.headerImage', defaultMessage: 'Image'},
  headerVideo: {id: 'comms.templates.wa.headerVideo', defaultMessage: 'Video'},
  headerDocument: {id: 'comms.templates.wa.headerDocument', defaultMessage: 'Document'},
  headerText: {id: 'comms.templates.cfg.headerText', defaultMessage: 'Header Text'},
  buttonText: {id: 'comms.templates.cfg.buttonText', defaultMessage: 'Button Text'},
  livePreview: {id: 'comms.templates.preview.live', defaultMessage: 'Live Preview'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

// i18n-ok-start: `value` que viaja a Botmaker. Las etiquetas visibles salen de
// HEADER_LABELS; los codigos de locale se muestran como codigos a proposito.
const HEADER_TYPES = ['NONE', 'IMAGE', 'VIDEO', 'DOCUMENT'] as const
const LOCALE_OPTIONS = ['es', 'en']
// i18n-ok-end

const HEADER_LABELS: Record<string, MessageDescriptor> = {
  NONE: M.headerNone,
  IMAGE: M.headerImage,
  VIDEO: M.headerVideo,
  DOCUMENT: M.headerDocument,
}

const CATEGORY_OPTIONS = [
  'UTILITY',
  'MARKETING',
  'TRANSACTIONAL',
  'AUTO_REPLY',
  'ALERT_UPDATE',
  'APPOINTMENT_UPDATE',
  'AUTHENTICATION',
]

type ButtonConfig = {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
  text: string
  value: string // intentIdOrName, url, or phone number
}

interface ICreateBotmakerTemplateModalProps {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}

export const CreateBotmakerTemplateModal = ({
  show,
  onHide,
  onSuccess,
}: ICreateBotmakerTemplateModalProps) => {
  const intl = useIntl()
  const [form, setForm] = useState<{
    name: string
    locale: string
    category: string
    botName: string
    phoneNumber: string
    optInImage: string
    bodyText: string
    namespace: string
    headerType: 'NONE' | 'IMAGE' | 'VIDEO' | 'DOCUMENT'
    headerUrl: string
    footerText: string
    buttons: ButtonConfig[]
  }>({
    name: '',
    locale: 'es',
    category: 'UTILITY',
    botName: '',
    phoneNumber: '',
    optInImage: 'https://storage.googleapis.com/storage.botmaker.com/public/res/thefutureinsurance/20251211-ZR4fReIhcgcwoqjNyIzAGVtE3Gu1-TB30W-b3B0LWV2aWRlbmNpYS1ldmlkZW5jZQ==.jpg',
    bodyText: '',
    namespace: '',
    headerType: 'NONE',
    headerUrl: '',
    footerText: '',
    buttons: [],
  })

  const {data: settingsData} = useQuery(GET_BOTMAKER_SETTINGS)

  useEffect(() => {
    if (settingsData?.botmakerSettings) {
      setForm((prev) => ({
        ...prev, 
        namespace: settingsData.botmakerSettings.namespace || prev.namespace,
        botName: settingsData.botmakerSettings.botName || prev.botName,
        phoneNumber: settingsData.botmakerSettings.phoneLineNumber || prev.phoneNumber,
      }))
    }
  }, [settingsData])

  const [createTemplate, {loading: creating}] = useMutation(CREATE_BOTMAKER_WHATSAPP_TEMPLATE, {
    onCompleted: (res) => {
      console.info('Botmaker template create response', res)
      toast.success(intl.formatMessage(M.toastSubmitted))
      setForm({
        name: '',
        locale: 'es',
        category: 'UTILITY',
        botName: settingsData?.botmakerSettings?.botName || '',
        phoneNumber: settingsData?.botmakerSettings?.phoneLineNumber || '',
        optInImage: 'https://storage.googleapis.com/storage.botmaker.com/public/res/thefutureinsurance/20251211-ZR4fReIhcgcwoqjNyIzAGVtE3Gu1-TB30W-b3B0LWV2aWRlbmNpYS1ldmlkZW5jZQ==.jpg',
        bodyText: '',
        namespace: settingsData?.botmakerSettings?.namespace || '',
        headerType: 'NONE',
        headerUrl: '',
        footerText: '',
        buttons: [],
      })
      onSuccess()
      onHide()
    },
    onError: (err) => {
      console.error('Botmaker template create error', err)
      toast.error(err.message || intl.formatMessage(M.toastCreateError))
    },
  })

  const handleCreate = () => {
    if (!form.bodyText) {
      toast.error(intl.formatMessage(M.toastBodyRequired))
      return
    }

    const payload: any = {
      name: form.name || null,
      locale: form.locale,
      category: form.category,
      botName: form.botName,
      phoneLineNumber: form.phoneNumber,
      optInImage: form.optInImage,
      bodyText: form.bodyText,
    }

    if (form.footerText) {
      payload.footer = JSON.stringify({text: form.footerText})
    }

    if (form.headerType !== 'NONE') {
      const headerObj = {
        format: form.headerType,
        exampleURL: form.headerUrl || 'https://via.placeholder.com/300',
      }
      payload.header = JSON.stringify(headerObj)
    }

    if (form.buttons.length > 0) {
      const buttonsArr = form.buttons.map((b) => {
        if (b.type === 'QUICK_REPLY') {
          return {
            type: 'QUICK_REPLY',
            text: b.text,
            intentIdOrName: b.value,
          }
        }
        return {
          type: b.type,
          text: b.text,
          url: b.type === 'URL' ? b.value : undefined,
          phoneNumber: b.type === 'PHONE_NUMBER' ? b.value : undefined,
        }
      })
      payload.buttons = JSON.stringify(buttonsArr)
    }

    createTemplate({
      variables: {
        input: payload,
      },
    })
  }

  return (
    <Modal show={show} onHide={onHide} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>
            <div className='title-botmaker-template'>
                <h3>{intl.formatMessage(M.title)}</h3>
                <span className='text-muted'>
                  {intl.formatMessage(M.botLine, {name: form.botName, phone: form.phoneNumber})}
                </span>
            </div>
            </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='row'>
          {/* Left Column: Form Configuration */}
          <div className='col-lg-8'>
            <div className='d-flex flex-wrap gap-3 mb-4'>
              <Form.Group style={{minWidth: 200}}>
                <Form.Label>{intl.formatMessage(M.name)}</Form.Label>
                <Form.Control
                  placeholder={intl.formatMessage(M.namePlaceholder)}
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                />
              </Form.Group>
              <Form.Group style={{minWidth: 100}}>
                <Form.Label>{intl.formatMessage(M.locale)}</Form.Label>
                <Form.Select
                  value={form.locale}
                  onChange={(e) => setForm({...form, locale: e.target.value})}
                >
                  {LOCALE_OPTIONS.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group style={{minWidth: 150}}>
                <Form.Label>{intl.formatMessage(M.category)}</Form.Label>
                <Form.Select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='flex-grow-1'>
                <Form.Label>{intl.formatMessage(M.namespace)}</Form.Label>
                <Form.Control
                  placeholder={intl.formatMessage(M.namespacePlaceholder)}
                  value={form.namespace}
                  onChange={(e) => setForm({...form, namespace: e.target.value})}
                  readOnly={!!settingsData?.botmakerSettings?.namespace}
                  disabled={!!settingsData?.botmakerSettings?.namespace}
                />
              </Form.Group>
            </div>

            {/* Header Config */}
            <div className='mb-4 border-bottom pb-3'>
              <Form.Label className='fw-bold'>{intl.formatMessage(M.headerSection)}</Form.Label>
              <div className='d-flex gap-3 align-items-end'>
                <Form.Group style={{minWidth: 150}}>
                  <Form.Select
                    value={form.headerType}
                    onChange={(e) => setForm({...form, headerType: e.target.value as any})}
                  >
                    {HEADER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {intl.formatMessage(HEADER_LABELS[type])}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {form.headerType !== 'NONE' && (
                  <Form.Group className='flex-grow-1'>
                    <Form.Control
                      placeholder={intl.formatMessage(M.headerUrlPlaceholder)}
                      value={form.headerUrl}
                      onChange={(e) => setForm({...form, headerUrl: e.target.value})}
                    />
                  </Form.Group>
                )}
              </div>
            </div>

            {/* Body */}
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold'>{intl.formatMessage(M.bodyMessage)}</Form.Label>
              <WhatsAppEditor
                value={form.bodyText}
                onChange={(text) => setForm({...form, bodyText: text})}
                height={200}
                placeholder={intl.formatMessage(M.bodyPlaceholder)}
                variables={[...BOTMAKER_VARIABLES, ...DEFAULT_TEMPLATE_VARIABLES]}
                getVariableText={(v) => `\${${v}}`}
              />
            </Form.Group>

            {/* Footer */}
            <Form.Group className='mb-4'>
              <Form.Label className='fw-bold'>{intl.formatMessage(M.footerOptional)}</Form.Label>
              <Form.Control
                placeholder={intl.formatMessage(M.footerPlaceholder)}
                value={form.footerText}
                onChange={(e) => setForm({...form, footerText: e.target.value})}
              />
            </Form.Group>

            {/* Buttons */}
            <div className='mb-4'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <Form.Label className='fw-bold mb-0'>{intl.formatMessage(M.buttons)}</Form.Label>
                <Button
                  variant='light'
                  size='sm'
                  onClick={() =>
                    setForm({
                      ...form,
                      buttons: [...form.buttons, {type: 'QUICK_REPLY', text: '', value: ''}],
                    })
                  }
                  disabled={form.buttons.length >= 3}
                >
                  <i className='bi bi-plus-circle me-1' /> {intl.formatMessage(M.addButton)}
                </Button>
              </div>

              {form.buttons.map((btn, idx) => (
                <div
                  key={idx}
                  className='d-flex gap-2 mb-2 align-items-start bg-light p-2 rounded'
                >
                  <Form.Select
                    style={{width: '140px'}}
                    size='sm'
                    value={btn.type}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].type = e.target.value as any
                      setForm({...form, buttons: newButtons})
                    }}
                  >
                    <option value='QUICK_REPLY'>{intl.formatMessage(M.quickReply)}</option>
                    {/* <option value="URL">URL</option>
                              <option value="PHONE_NUMBER">Phone</option> */}
                  </Form.Select>

                  <Form.Control
                    placeholder={intl.formatMessage(M.buttonText)}
                    size='sm'
                    value={btn.text}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].text = e.target.value
                      setForm({...form, buttons: newButtons})
                    }}
                  />

                  <Form.Control
                    placeholder={intl.formatMessage(
                      btn.type === 'QUICK_REPLY' ? M.intentPlaceholder : M.valuePlaceholder
                    )}
                    size='sm'
                    value={btn.value}
                    onChange={(e) => {
                      const newButtons = [...form.buttons]
                      newButtons[idx].value = e.target.value
                      setForm({...form, buttons: newButtons})
                    }}
                  />

                  <Button
                    variant='outline-danger'
                    size='sm'
                    onClick={() => {
                      const newButtons = form.buttons.filter((_, i) => i !== idx)
                      setForm({...form, buttons: newButtons})
                    }}
                  >
                    <i className='bi bi-trash'></i>
                  </Button>
                </div>
              ))}
              {form.buttons.length === 0 && (
                <div className='text-muted small fst-italic'>
                  {intl.formatMessage(M.noButtons)}
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Live Preview */}
          <div className='col-lg-4'>
            <Form.Label className='fw-bold'>{intl.formatMessage(M.livePreview)}</Form.Label>
            <WhatsAppPreview
              message={form.bodyText || intl.formatMessage(M.previewPlaceholder)}
              header={
                form.headerType !== 'NONE'
                  ? {
                      type: form.headerType as any,
                      url: form.headerUrl || undefined,
                      text: intl.formatMessage(M.headerText),
                    }
                  : undefined
              }
              footer={form.footerText}
              buttons={form.buttons.map((b) => ({
                type: b.type,
                text: b.text || intl.formatMessage(M.buttonFallback),
              }))}
            />
          </div>
        </div>
        <div className='mb-4'>
            <Form.Group>
            <Form.Label>{intl.formatMessage(M.optInImage)}</Form.Label>
            <Form.Control
                type='url'
                placeholder={intl.formatMessage(M.optInImagePlaceholder)}
                value={form.optInImage}
                onChange={(e) => setForm({...form, optInImage: e.target.value})}
            />
            <Form.Text className='text-muted'>
                {intl.formatMessage(M.optInImageHint)}
            </Form.Text>
            </Form.Group>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button onClick={handleCreate} disabled={creating} variant='primary'>
          {creating ? <Spinner animation='border' size='sm' className='me-2' /> : null}
          {intl.formatMessage(M.submit)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
